import React, { useEffect, useMemo, useState } from 'react';
import { Redirect, useHistory, useLocation, useParams } from 'react-router-dom';
import { Alert, Box, Button, Tooltip, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import BreadCrumbs from '../../Common/BreadCrumbs';
import useNavigate from '../../../hooks/useNavigate';
import Routes from '../../../routes';
import { SURFACE_NAMES, SURFACE_ORDER, findDrillDown } from '../regulationConfig';
import { useComplianceFilter } from '../ComplianceFilterContext';
import PageFilterBar from '../PageFilterBar';
import CalcGuideDialog from '../CalcGuideDialog';
import SurfaceTile from './SurfaceTile';
import SurfaceTable from './SurfaceTable';

const DEFAULT_BANDS = ['Near', 'Hit'];

// AB#17128: regulation drill-down template; EPA 608 is the first configuration (regulationConfig DRILL_DOWNS)
const RegulationPage = () => {
    const { regulation: slug } = useParams();
    const regulation = findDrillDown(slug);
    const history = useHistory();
    const location = useLocation();
    const { requestFilters } = useComplianceFilter();

    useNavigate({ pageName: 'ComplianceCenter' });

    // Only an enabled surface of this regulation can be selected; anything else falls back to In-Scope
    const selected = new URLSearchParams(location.search).get('surface');
    const selectable = regulation && Object.prototype.hasOwnProperty.call(regulation.surfaces, selected)
        && !regulation.surfaces[selected].disabledReason && !regulation.surfaces[selected].dataDependency;
    const surfaceKey = selectable ? selected : 'inScope';

    const [overview, setOverview] = useState(null);
    const [overviewFailed, setOverviewFailed] = useState(false);
    const [overviewAttempt, setOverviewAttempt] = useState(0);
    const [unfilteredOverview, setUnfilteredOverview] = useState(null);
    const [bandsBySurface, setBandsBySurface] = useState({});
    const [calcGuideKey, setCalcGuideKey] = useState(null);

    const lowOn = useMemo(
        () => Object.entries(bandsBySurface).filter(([, b]) => b.includes('Low')).map(([key]) => ({ property: 'lowOn', value: key })),
        [bandsBySurface]
    );
    const overviewKey = JSON.stringify([requestFilters, lowOn]);

    useEffect(() => {
        if (!regulation) return undefined;
        const controller = new AbortController();
        // A new request clears the previous failure; it is set again only if this one fails
        setOverviewFailed(false);
        const load = async () => {
            try {
                const response = await fetch(Routes.ComplianceCenterRegulation.Overview(regulation.key), {
                    method: 'POST',
                    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
                    body: JSON.stringify({ filters: [...requestFilters, ...lowOn] }),
                    signal: controller.signal,
                });
                // A failed reply must not leave the previous filter's counts on screen, and says so (N2)
                const data = response.ok ? await response.json() : null;
                if (controller.signal.aborted) return;
                setOverview(data);
                setOverviewFailed(!data);
            } catch (error) {
                // Aborted or cancelled by leaving the page: nothing to show; otherwise the inline message covers it (N6)
                if (controller.signal.aborted) return;
                console.warn('Compliance Center overview did not load', error);
                setOverview(null);
                setOverviewFailed(true);
            }
        };
        load();
        return () => controller.abort();
    }, [regulation?.key, overviewKey, overviewAttempt]);

    // AC11: counts without the page filter, only while one is set, so an emptied table can say how many it hides
    const unfilteredKey = JSON.stringify([requestFilters.length > 0, lowOn]);
    useEffect(() => {
        if (!regulation || requestFilters.length === 0) {
            setUnfilteredOverview(null);
            return undefined;
        }
        const controller = new AbortController();
        fetch(Routes.ComplianceCenterRegulation.Overview(regulation.key), {
            method: 'POST',
            headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
            body: JSON.stringify({ filters: lowOn }),
            signal: controller.signal,
        })
            .then((response) => (response.ok ? response.json() : null))
            .then((data) => { if (!controller.signal.aborted) setUnfilteredOverview(data); })
            .catch(() => { if (!controller.signal.aborted) setUnfilteredOverview(null); });
        return () => controller.abort();
    }, [regulation?.key, unfilteredKey]);

    if (!regulation) {
        return <Redirect to="/ComplianceCenter" />;
    }

    const countsByKey = Object.fromEntries((overview?.surfaces || []).map((s) => [s.key, s]));
    // Undefined while loading or after a failure, which SurfaceTable reads as "count unknown" (N3)
    const unfilteredCountsByKey = Object.fromEntries((unfilteredOverview?.surfaces || []).map((s) => [s.key, s.key === 'inScope' ? unfilteredOverview.inScopeCount : s.count]));
    const surfaces = SURFACE_ORDER.map((key) => ({ key, ...(regulation.surfaces[key] || { name: SURFACE_NAMES[key], disabledReason: `Not defined under ${regulation.name}` }) }));
    const surface = regulation.surfaces[surfaceKey];
    const calcGuideSurface = calcGuideKey ? regulation.surfaces[calcGuideKey] : null;

    const selectSurface = (key) => history.replace({ pathname: location.pathname, search: `?surface=${key}` });

    return (
        <>
            <BreadCrumbs
                path={[
                    { text: 'Compliance Center', to: '/ComplianceCenter' },
                    { text: regulation.name },
                ]}
            />
            <Box sx={{ mb: 2 }}>
                <Typography variant="h4" component="h1" sx={{ fontSize: '1.5rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}>
                    {regulation.name}
                    <Tooltip title={regulation.citation}>
                        <InfoOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} aria-label={`Citation: ${regulation.citation}`} titleAccess={`Citation: ${regulation.citation}`} tabIndex={0} />
                    </Tooltip>
                </Typography>
                <Typography sx={{ fontSize: '14px', color: 'text.secondary', mt: '2px' }}>
                    {overview ? `${overview.inScopeCount.toLocaleString()} in-scope assets` : (overviewFailed ? 'In-scope count unavailable' : ' ')}
                </Typography>
                <Typography sx={{ fontSize: '14px', color: 'text.primary', mt: 1, maxWidth: '900px' }}>
                    {regulation.copy.summary}
                </Typography>
            </Box>
            <PageFilterBar />
            {overviewFailed && (
                <Alert
                    severity="warning"
                    sx={{ mb: 2 }}
                    action={<Button color="inherit" size="small" onClick={() => setOverviewAttempt((n) => n + 1)}>Try again</Button>}
                >
                    The tile counts for this page could not be loaded.
                </Alert>
            )}
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))', lg: 'repeat(6, minmax(0, 1fr))' },
                    gap: '12px',
                }}
            >
                {surfaces.map((tile) => {
                    const counts = countsByKey[tile.key];
                    return (
                        <SurfaceTile
                            key={tile.key}
                            surface={tile}
                            counts={counts && overview && { ...counts, count: tile.key === 'inScope' ? overview.inScopeCount : counts.count }}
                            selected={tile.key === surfaceKey}
                            onSelect={() => selectSurface(tile.key)}
                            onInfo={() => setCalcGuideKey(tile.key)}
                        />
                    );
                })}
            </Box>
            <SurfaceTable
                key={`${regulation.key}-${surfaceKey}`}
                regulation={regulation}
                surfaceKey={surfaceKey}
                surface={surface}
                copy={regulation.copy.surfaces[surfaceKey]}
                valueOptions={regulation.valueOptions}
                pageFilters={requestFilters}
                unfilteredCount={unfilteredCountsByKey[surfaceKey]}
                bands={bandsBySurface[surfaceKey] || DEFAULT_BANDS}
                onBandsChange={(bands) => setBandsBySurface((prev) => ({ ...prev, [surfaceKey]: bands }))}
            />
            {calcGuideSurface && (
                <CalcGuideDialog
                    open
                    onClose={() => setCalcGuideKey(null)}
                    title={`${calcGuideSurface.name} — how this is calculated`}
                    sections={regulation.copy.surfaces[calcGuideKey]?.calcGuide || []}
                />
            )}
        </>
    );
};

export default RegulationPage;
